import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, MapPin, ChevronDown, Sofa, Laptop, Shirt, Baby, Wrench, Car, PawPrint, Briefcase, HandCoins, Package, Star, ArrowRight, Shield, MessageCircle, CreditCard, Dumbbell, Gamepad2, Home, Gift, ArrowLeftRight, Clock, Tag, Calendar, Megaphone, Building } from 'lucide-react';
import { listingsAPI, metaAPI, recentlyViewedAPI, businessAdsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import ListingCard from '../components/ListingCard';
import BusinessAdCard from '../components/BusinessAdCard';

const ICON_MAP = { Sofa, Laptop, Shirt, Baby, Wrench, Car, PawPrint, Briefcase, HandCoins, Package, Dumbbell, Gamepad2, Home, Gift, ArrowLeftRight, Tag, Calendar, Search, Megaphone, Building };

const COMMUNITY_SECTIONS = [
  { id: 'garage_sales', label: 'Garage Sales', icon: Tag, color: 'bg-emerald-50 text-emerald-600', type: 'garage_sale' },
  { id: 'local_events', label: 'Local Events', icon: Calendar, color: 'bg-violet-50 text-violet-600', type: 'event' },
  { id: 'jobs', label: 'Jobs & Gigs', icon: HandCoins, color: 'bg-amber-50 text-amber-600', type: 'job' },
  { id: 'services', label: 'Services', icon: Briefcase, color: 'bg-blue-50 text-blue-600', type: 'service' },
  { id: 'lost_found', label: 'Lost & Found', icon: Search, color: 'bg-red-50 text-red-600', type: 'lost_found' },
  { id: 'announcements', label: 'Announcements', icon: Megaphone, color: 'bg-orange-50 text-orange-600', type: 'announcement' },
  { id: 'free_stuff', label: 'Free Stuff', icon: Gift, color: 'bg-pink-50 text-pink-600', type: 'sale' },
  { id: 'rentals', label: 'Rentals', icon: Home, color: 'bg-teal-50 text-teal-600', type: 'rental' },
  { id: 'vehicles', label: 'Vehicles', icon: Car, color: 'bg-indigo-50 text-indigo-600', type: 'vehicle' },
  { id: 'real_estate', label: 'Real Estate', icon: Building, color: 'bg-rose-50 text-rose-600', type: 'real_estate' },
];

const HERO_IMG = "https://images.unsplash.com/photo-1605146768851-eda79da39897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxkdXJoYW0lMjByZWdpb24lMjBvbnRhcmlvfGVufDB8fHx8MTc3NTE1MDI1OHww&ixlib=rb-4.1.0&q=85&w=1920";
const COMMUNITY_IMG = "https://images.unsplash.com/photo-1504211910536-32e00cbd7cbf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxoYXBweSUyMGxvY2FsJTIwY29tbXVuaXR5fGVufDB8fHx8MTc3NTE1MDI1OHww&ixlib=rb-4.1.0&q=85&w=800";

const CITIES = ["Pickering", "Ajax", "Whitby", "Courtice", "Oshawa", "Bowmanville"];

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [recentListings, setRecentListings] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [businessAds, setBusinessAds] = useState([]);
  const [activeAdIdx, setActiveAdIdx] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  useEffect(() => {
    metaAPI.getCategories().then(r => setCategories(r.data)).catch(() => {});
    listingsAPI.getAll({ limit: 4, promoted_only: true }).then(r => {
      const featured = r.data.listings || [];
      setFeaturedListings(featured);
      // Track impressions for featured listings
      const ids = featured.filter(l => l.promoted).map(l => l.listing_id);
      if (ids.length > 0) listingsAPI.trackImpressions(ids).catch(() => {});
    }).catch(() => {});
    listingsAPI.getAll({ limit: 8, sort: 'newest' }).then(r => setRecentListings(r.data.listings || [])).catch(() => {});
    businessAdsAPI.getPublic(10).then(r => setBusinessAds(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) recentlyViewedAPI.getAll(8).then(r => setRecentlyViewed(r.data)).catch(() => {});
  }, [user]);

  // Rotating banner carousel
  useEffect(() => {
    if (businessAds.length <= 1) return;
    const interval = setInterval(() => setActiveAdIdx(prev => (prev + 1) % businessAds.length), 5000);
    return () => clearInterval(interval);
  }, [businessAds.length]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedCity) params.set('city', selectedCity);
    navigate(`/browse?${params.toString()}`);
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden bg-zinc-900" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Durham Region" className="w-full h-full object-cover opacity-30" />
        </div>
        <div className="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-24 sm:py-32 lg:py-40">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4 animate-fade-in-up">Durham Region Marketplace</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none text-white mb-6 animate-fade-in-up stagger-1">
              Buy, sell & connect<br />in your neighbourhood
            </h1>
            <p className="text-base sm:text-lg text-zinc-300 mb-8 max-w-xl animate-fade-in-up stagger-2">
              The trusted marketplace for Pickering, Ajax, Whitby, Courtice, Oshawa & Bowmanville. Find local deals, services, and opportunities.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 animate-fade-in-up stagger-3" data-testid="hero-search-form">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search furniture, electronics, services..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 rounded-full bg-white text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#002FA7] text-sm"
                  data-testid="hero-search-input"
                />
              </div>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowCityDropdown(!showCityDropdown)}
                  className="w-full sm:w-auto flex items-center gap-2 px-5 py-3.5 rounded-full bg-white/10 backdrop-blur text-white border border-white/20 hover:bg-white/20 transition-colors text-sm font-medium"
                  data-testid="city-selector-btn"
                >
                  <MapPin className="w-4 h-4" />
                  {selectedCity || 'All Cities'}
                  <ChevronDown className="w-4 h-4" />
                </button>
                {showCityDropdown && (
                  <div className="absolute top-full mt-2 w-full sm:w-48 bg-white rounded-lg shadow-xl border border-zinc-200 z-50 py-1">
                    <button
                      onClick={() => { setSelectedCity(''); setShowCityDropdown(false); }}
                      className="w-full text-left px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
                      data-testid="city-option-all"
                    >All Cities</button>
                    {CITIES.map(c => (
                      <button
                        key={c}
                        onClick={() => { setSelectedCity(c); setShowCityDropdown(false); }}
                        className="w-full text-left px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
                        data-testid={`city-option-${c.toLowerCase()}`}
                      >{c}</button>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="submit"
                className="bg-[#002FA7] text-white hover:bg-[#002585] transition-colors rounded-full px-8 py-3.5 font-bold text-sm tracking-wide"
                data-testid="hero-search-btn"
              >Search</button>
            </form>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16 sm:py-24" data-testid="categories-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Browse</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900">Categories</h2>
          </div>
          <Link to="/browse" className="text-sm font-bold text-[#002FA7] hover:underline flex items-center gap-1" data-testid="view-all-categories-link">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7 gap-3">
          {categories.map((cat, i) => {
            const IconComp = ICON_MAP[cat.icon] || Package;
            return (
              <Link
                key={cat.id}
                to={`/browse?category=${cat.id}`}
                className="group flex flex-col items-center gap-2 p-4 rounded-lg border border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-md hover:-translate-y-1 transition-all duration-200 animate-fade-in-up"
                data-testid={`category-card-${cat.id}`}
              >
                <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center group-hover:bg-[#002FA7]/10 transition-colors">
                  <IconComp className="w-5 h-5 text-zinc-600 group-hover:text-[#002FA7] transition-colors" />
                </div>
                <span className="text-xs font-semibold text-zinc-800 text-center leading-tight">{cat.label}</span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Community Sections */}
      <section className="bg-[#FAFAFA] px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="community-sections">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Explore</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900">Community Hub</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {COMMUNITY_SECTIONS.map(sec => {
              const Icon = sec.icon;
              return (
                <Link
                  key={sec.id}
                  to={`/browse?category=${sec.id}`}
                  className="group flex items-center gap-3 p-4 rounded-lg bg-white border border-zinc-200 hover:border-zinc-300 hover:shadow-md transition-all duration-200"
                  data-testid={`community-${sec.id}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${sec.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-semibold text-zinc-800 leading-tight">{sec.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured/Promoted Listings */}
      {featuredListings.length > 0 && (
        <section className="bg-[#F5F5F7] px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="featured-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-2">Spotlight</p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900">Featured Listings</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredListings.map(l => <ListingCard key={l.listing_id} listing={l} promoted />)}
            </div>
          </div>
        </section>
      )}

      {/* Sponsored Business Ad Banner Carousel */}
      {businessAds.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8" data-testid="business-ads-banner-section">
          <div className="relative">
            <BusinessAdCard ad={businessAds[activeAdIdx]} variant="banner" />
            {businessAds.length > 1 && (
              <div className="flex items-center justify-center gap-1.5 mt-3">
                {businessAds.map((_, i) => (
                  <button key={i} onClick={() => setActiveAdIdx(i)}
                    className={`w-2 h-2 rounded-full transition-all ${i === activeAdIdx ? 'bg-[#002FA7] w-6' : 'bg-zinc-300 hover:bg-zinc-400'}`}
                    data-testid={`ad-dot-${i}`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Recent Listings */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16 sm:py-24" data-testid="recent-listings-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Fresh Finds</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900">Recent Listings</h2>
          </div>
          <Link to="/browse" className="text-sm font-bold text-[#002FA7] hover:underline flex items-center gap-1" data-testid="view-all-listings-link">
            Browse all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {recentListings.map(l => <ListingCard key={l.listing_id} listing={l} />)}
        </div>
        {recentListings.length === 0 && (
          <div className="text-center py-16">
            <p className="text-zinc-500 text-lg mb-4">No listings yet. Be the first to post!</p>
            <Link to="/post" className="bg-[#002FA7] text-white hover:bg-[#002585] transition-colors rounded-full px-8 py-3 font-bold text-sm" data-testid="first-listing-cta">
              Post a Listing
            </Link>
          </div>
        )}
      </section>

      {/* Recently Viewed */}
      {user && recentlyViewed.length > 0 && (
        <section className="bg-[#FAFAFA] px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="recently-viewed-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Pick up where you left off</p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900 flex items-center gap-3">
                  Recently Viewed
                  <Clock className="w-6 h-6 text-zinc-400" />
                </h2>
              </div>
              <Link to="/browse" className="text-sm font-bold text-[#002FA7] hover:underline flex items-center gap-1" data-testid="view-all-recently-viewed-link">
                Browse more <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="recently-viewed-grid">
              {recentlyViewed.map(l => <ListingCard key={l.listing_id} listing={l} />)}
            </div>
          </div>
        </section>
      )}

      {/* Trust Section */}
      <section className="bg-zinc-900 text-white" data-testid="trust-section">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16 sm:py-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4">Why Durham Market</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-8">Built for your community</h2>
              <div className="space-y-6">
                {[
                  { icon: Shield, title: "Verified Sellers", desc: "Every seller is verified for your safety" },
                  { icon: MessageCircle, title: "Secure Messaging", desc: "Chat directly without sharing personal info" },
                  { icon: CreditCard, title: "Safe Payments", desc: "Pay securely via Stripe or meet in person" },
                  { icon: MapPin, title: "Hyper-Local", desc: "Find deals in your exact neighbourhood" },
                ].map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#002FA7]/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#002FA7]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white mb-1">{title}</h3>
                      <p className="text-sm text-zinc-400">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img src={COMMUNITY_IMG} alt="Community" className="rounded-lg w-full object-cover aspect-[4/3]" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16 sm:py-24 text-center" data-testid="cta-section">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900 mb-4">Ready to get started?</h2>
        <p className="text-base text-zinc-600 mb-8 max-w-lg mx-auto">Join thousands of Durham Region residents buying, selling, and connecting every day.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to={user ? "/post" : "/auth?mode=register"}
            className="bg-[#002FA7] text-white hover:bg-[#002585] transition-colors rounded-full px-8 py-3 font-bold text-sm inline-flex items-center justify-center"
            data-testid="cta-post-btn"
          >Post a Free Ad</Link>
          <Link
            to="/browse"
            className="bg-white text-zinc-900 border border-zinc-200 hover:bg-zinc-50 transition-colors rounded-full px-8 py-3 font-bold text-sm inline-flex items-center justify-center"
            data-testid="cta-browse-btn"
          >Browse Listings</Link>
        </div>
      </section>
    </div>
  );
}
